import { useEffect, useRef, useState } from 'react';
import { zh } from './i18n.js';
import { go } from './nav.js';
import { returnParam } from './gate.jsx';
import { call, getToken } from './api.js';
import { TabBar, SearchBar, useSpeech } from './ui.jsx';
/* ⚠️ WRITTEN THROUGH THE ONE MODULE, NOT STRAIGHT INTO STORAGE.
   This screen used to `localStorage.setItem` directly — correct, and silent.
   Home holds the pin in React state, so changing your place here left the pin
   on the previous answer until the app was reloaded. `writeCentre` stores it
   AND tells every screen watching, which is the same fault the rails, the
   heart and this all share: one place knows, and nothing tells the others. */
import { writeCentre, centreFrom, readCentre, marketFor, FALLBACK_MARKET,
  findMe as locate, browsingName, countryName } from './place.js';

/* ══════════════════════════════════════════════════════════════════════════
 * LOCATION — `08j_Location.html`
 *
 * ⚠️ SEVEN ROUTES WITH NO SCREEN. /where · /places · /places/centre ·
 * /my-place · /near-me-presets · /markets · /locations all existed and worked,
 * and nothing in the app called any of them. The backend for this was finished
 * long before tonight; only the screen was missing.
 *
 * ── ⚠️ THE ONE SENTENCE THAT EXPLAINS THIS WHOLE SCREEN ─────────────────────
 *
 * The locked screen says it, and everything below follows from it:
 *
 *     **"Where you are and where you are looking are two different things."**
 *
 * An Eleater in Wan Chai planning Saturday in Kennedy Town is not lost. The
 * browsing centre is a CHOICE, and the phone's GPS is only ever the default
 * offer — never an override.
 *
 * ── AND THE RESOLVER NEVER GUESSES ──────────────────────────────────────────
 *
 * ⚠️ `/places` PREFERS ELEAT'S OWN AREAS AND IS NOT LIMITED TO THEM. The route
 * says why, from a real failure: a resolver that only knew our rows *"told Al
 * he was in 'North' while standing in Shenzhen."* And `/places/centre` may
 * legitimately answer with a null market — centring on somewhere Eleat has no
 * venues returns that place and an honest empty list, never a silent
 * relocation to a city we do cover.
 *
 * ⚠️ NO COORDINATE IS STORED AGAINST AN ELEATER. /where's own note: the only
 * thing written is a ~110m rounded key in `geocode_cache`, recording what a
 * patch of the world is called, attached to nobody. This screen asks the
 * browser for a position, sends it once, and keeps none of it.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️⚠️ `const MARKET = 'hong-kong'` WAS HERE TOO, and on this screen of all
   screens. Typing "Kensington" while standing in Calgary searched Hong Kong's
   places and its near-me presets — the one screen whose entire job is that
   where you are and where you are looking are two different things.

   ⚠️ THE FALLBACK IS ONLY FOR HAVING NOTHING AT ALL. A centre with a null
   market is a real place Eleat is not in, and the place SEARCH still has to
   work there — that is how somebody in Singapore reaches Hong Kong. So this
   screen, and only this screen, searches the launch market when it has no
   market of its own, because otherwise there is no way back in. */
const KEY = 'eleat.centre';

export default function Location() {
  const [centre, setCentre] = useState(null);     // {label, market, …}
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [presets, setPresets] = useState([]);
  /* ⚠️ THE AREAS OF THE CITY BEING BROWSED — see the section below.
     `[]` not null: the section is hidden until there is something in it, so a
     screen that has not answered yet shows nothing rather than a heading over
     a gap. */
  const [areasBy, setAreasBy] = useState({});      // city code → its areas
  const [openCountry, setOpenCountry] = useState(null);
  const [openCity, setOpenCity] = useState(null);
  const [finding, setFinding] = useState(false);
  const [err, setErr] = useState(null);
  /* the same microphone and the same four answers as Home's bar */
  const [micSaid, setMicSaid] = useState(null);
  const speech = useSpeech({
    onPartial: (t) => { setMicSaid(null); setQ(t); },
    onDone: (t) => { if (t) setQ(t); },
    onQuiet: () => setMicSaid({ text: "I didn't catch that. Try again, or type it.", bad: false }),
    onError: (e) => setMicSaid({ text: e.message, bad: true }),
  });
  const seq = useRef(0);
  /* ⚠️ WHAT THE PLACE SEARCH IS SCOPED TO — see the note above the KEY. */
  const searchMarket = marketFor(readCentre()) || FALLBACK_MARKET;


  useEffect(() => {
    /* ⚠️ WHAT WAS ALREADY CHOSEN COMES BACK. A location screen that forgets
       is one an Eleater has to set every single time they open it. */
    try {
      const saved = localStorage.getItem(KEY);
      if (saved) setCentre(JSON.parse(saved));
    } catch { /* storage can be off; it is a convenience, not a requirement */ }

    call('/discovery/markets')
      .then((d) => setMarkets(d.markets || []))
      .catch(() => setMarkets([]));
    /* ⚠️⚠️ `with_venues=1` — WHERE ELEAT ACTUALLY HAS FOOD, NOT THE TOP OF THE
       TREE. This asked for the top level first, and this comment claimed that
       was right *"whatever shape this market uses"*. **It was wrong, and the
       seed says so:** Hong Kong is 4 regions → 18 districts → 59 areas, so the
       top level is four chips reading *Hong Kong Island · Kowloon · New
       Territories · Islands* — nothing like Al's *"Hong Kong by district"* — and
       Calgary has no district level at all, so naming a level breaks one market
       or the other. The route returns the most specific places that have
       restaurants, with a count each, and the DATA decides the level.
       CLAUDE.md: *"Never add region/district columns — that forces every market
       into Hong Kong's shape."*  A screen is held to the same rule. */
    loadAreas(searchMarket);
    setOpenCity(searchMarket);
    call(`/discovery/near-me-presets?market=${searchMarket}`)
      .then((d) => setPresets(Array.isArray(d) ? d : (d.presets || d.near_me || [])))
      .catch(() => setPresets([]));

    /* ⚠️ THE ADDRESS THEY ALREADY GAVE US, as a fallback and never as the
       answer. The route's own words: "A restaurant has one fixed address; an
       Eleater moves. Where the phone is beats where they live, every time." */
    if (getToken()) {
      call('/discovery/my-place', { auth: true })
        /* ══════════════════════════════════════════════════════════════
           ⚠️⚠️ THIS READ `d.label` AND THE ROUTE SENDS `{ place: { label } }`.
           So `d.label` was `undefined`, the `if` never passed, and the
           saved-address fallback has never once run since it was written.
           **Fourth screen caught reading a field name the server does not
           send** — the pin, the city chips, the venue page, now this.
           `centreFrom()` is the one function that knows the shape; a fifth
           hand-written unwrap here is how there came to be four. */
        .then((d) => {
          const p = centreFrom(d, { from: 'saved' });
          if (p && p.label) setCentre((c) => c || p);
        })
        .catch(() => { /* no saved address — nothing to fall back to */ });
    }
  }, []);

  /* A city's areas, asked once per city when it is opened in the list.
     ⚠️ THE FILTER STAYS EVEN THOUGH THE QUERY ALREADY HAS `having > 0`: an
     area with nothing in it is a chip that lands on an empty list. */
  function loadAreas(code) {
    if (!code || areasBy[code]) return;
    call(`/discovery/locations?market=${code}&with_venues=1`)
      .then((d) => setAreasBy((m) => ({ ...m,
        [code]: (d.locations || []).filter((a) => a.venues > 0) })))
      .catch(() => setAreasBy((m) => ({ ...m, [code]: [] })));
  }

  /* The country of the city being browsed starts open, as OpenTable's list
     opens on the metro you are in. */
  useEffect(() => {
    const m = markets.find((x) => x.code === searchMarket);
    if (m && openCountry === null) setOpenCountry(m.country);
  }, [markets]);

  /* Choosing a city: written through the one module, `from: 'chosen'` so the
     phone's GPS does not drag it back (followMe only moves a 'gps' centre). */
  function chooseCity(m) {
    const next = centreFrom({
      label: m.name_en || m.code,
      market: m.code,
      lat: Number(m.centre_lat),
      lng: Number(m.centre_lng),
      /* the city's country — joining's phone code and billing country read it */
      country_code: m.country,
    }, { from: 'chosen' });
    setCentre(next);
    writeCentre(next);
    setErr(null);
  }

  /* ── typing a place ─────────────────────────────────────────────────── */
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setHits([]); return; }
    const mine = ++seq.current;
    const t = setTimeout(() => {
      call(`/discovery/places?q=${encodeURIComponent(term)}&market=${searchMarket}`)
        .then((d) => {
          /* ⚠️ A STALE ANSWER IS DISCARDED. Typing "ken" then "kennedy" fires
             twice and they can land out of order — the same guard Explore
             needed. */
          if (mine !== seq.current) return;
          setHits(Array.isArray(d) ? d : (d.places || d.results || []));
        })
        .catch(() => { if (mine === seq.current) setHits([]); });
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  function choose(place) {
    const params = place.location_id
      ? `location_id=${encodeURIComponent(place.location_id)}`
      : `place_id=${encodeURIComponent(place.place_id || place.id)}`;
    const label = place.label || place.name_en || place.name;
    call(`/discovery/places/centre?${params}&label=${encodeURIComponent(label || '')}`)
      .then((c) => {
        /* ⚠️ ONE MODULE KNOWS THE SHAPE. This line used to re-attach the
           label by hand — the fix for the pin bug, sitting in this file,
           applied to the one path that did not need it while both GPS paths
           went without it. `centreFrom` is that rule, in one place. */
        const next = centreFrom(c, { label: c.label || label });
        setCentre(next);
        setQ(''); setHits([]);
        writeCentre(next);
      })
      .catch((e) => setErr(e.message));
  }

  /* ── letting Eleat find you ─────────────────────────────────────────── */
  /* ⚠️ THE SAME `findMe` HOME USES (place.js) — accuracy sent, the nearest
     Eleat city kept — then Home, as choosing a city does. This screen had its
     own copy that did neither. */
  /* ⚠️ BACK TO WHERE THEY CAME FROM — Al, 6 Oct 2026: "when i do choose hk
     then it take me back to the homepage and does not open explore". */
  function back() { const r = returnParam(); return r === '?' ? '?home=1' : r; }
  function findMe() {
    setFinding(true); setErr(null);
    locate()
      .then(() => go(back()))
      .catch((e) => setErr(e.message))
      .finally(() => setFinding(false));
  }

  return (
    <div className="home loc">
      <div className="top">
        {/* ══════════════════════════════════════════════════════════════
            ⚠️⚠️⚠️ BACK MEANS BACK. THIS WAS `href="?profile=1"`.
            Al, 26 September 2026: *"then it goes into the profile. That
            should not happen because then there's no way of getting back…
            what if it's an eliter that hasn't signed up or signed in?"*

            He is right, and the hard-coded link was the smaller half of it.
            **Location is a BROWSING CHOICE, not an account setting.** Somebody
            who has never heard of Eleat opens it in Shenzhen and picks
            Admiralty; somebody in Bangkok looks at the Beltline. It has
            nothing to do with signing in and nothing to do with Profile — and
            sending every one of them to a Profile they may not have is a dead
            end at the end of the one screen that exists to open things up.

            ⚠️ `history.back()` WHEN THERE IS SOMEWHERE TO GO BACK TO, Home
            otherwise. A deep link, a shared URL or a cold open has no history,
            and `back()` on an empty stack does nothing at all — which is a
            button that looks alive and is not. Home is the honest floor.
            ══════════════════════════════════════════════════════════════ */}
        <a
          className="back"
          href="?home=1"
          aria-label="Back"
          onClick={(e) => {
            if (window.history.length > 1) { e.preventDefault(); window.history.back(); }
          }}
        >‹</a>
        <h1 className="heroline">Location</h1>
      </div>

      <main>
        {/* ══════════════════════════════════════════════════════════════
            ⚠️ THE SHAPE OF THE BIG APPS — Al, 1 October 2026: "make it look and
            work like the big apps". Read off their live sites that day:
            Uber Eats opens one sheet with ONE search box and a "Current
            location" row; OpenTable says one line and a small "Get current
            location" link with an arrow; OpenRice keeps the city a small
            control and puts districts as shortcuts. None of them explains.
            So: the search first, in Eleat's one search bar (magnifier · words
            · microphone, the 24 Sep shape); "Let Eleat find me" as a ROW with
            an arrow, not a slab of orange; where you are browsing only when
            there is a place to name; then the cities and the areas exactly as
            they were. The three sentences that explained the screen to the
            person using it are gone (Al's yes, 1 Oct).
            ══════════════════════════════════════════════════════════════ */}
        <SearchBar className="locsearch"
          value={q} onChange={setQ}
          onSubmit={() => { if (hits[0]) choose(hits[0]); }}
          placeholder="Search Eleat…"
          goLabel="Search"
          mic={speech.allowed
            ? { on: speech.hearing, onTap: speech.tap, label: 'Search by voice' }
            : null} />
        {micSaid && (
          <p className={micSaid.bad ? 'err' : 'mut'} role="status">{micSaid.text}</p>
        )}

        {hits.length > 0 && (
          <div className="plist">
            {hits.map((p) => (
              <button key={p.location_id || p.place_id || p.id}
                type="button" className="prowlink"
                onClick={() => choose(p)}>
                {p.label || p.name_en || p.name}
              </button>
            ))}
          </div>
        )}

        <div className="plist locfind">
          <button className="item" type="button" onClick={findMe} disabled={finding}>
            <span className="gi" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
                <path d="M21 3 3 10.5l7.5 3L13.5 21z" />
              </svg>
            </span>
            <span className="lab"><span className="t">{finding ? 'Checking…' : 'Let Eleat find me'}</span></span>
          </button>
        </div>

        {err && <div className="err" aria-live="polite">{err}</div>}

        {browsingName(centre) && (
          <>
            <div className="sect">WHERE YOU ARE BROWSING</div>
            <p className="lead">{browsingName(centre)}</p>
            {centre.market === null && (
              <p className="mut">{"Eleat isn't in your location yet."}</p>
            )}
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            ⚠️ COUNTRY → CITY → AREA, OPENTABLE'S OWN SHAPE — Al, 2 Oct 2026:
            *"we should be able to click an country and city like opentable."*
            Read off opentable.ca the same day: its location menu is metros
            with their regions and a count beside each, and "Full list of
            locations" is every country, each opening to its cities. Here:
            tap a country → its cities; tap a city → "All {city}" (the
            market's own `all_market_label_en`) and its areas with counts.
            "All …" browses that city on Home; an area opens Explore filtered
            to it, in that city. Every country and city is
            `/discovery/markets` — nothing typed in. The city being browsed is
            lit. This replaces the city chips and the separate areas section,
            so there is one way to choose a place, not three.
            ══════════════════════════════════════════════════════════════════ */}
        {markets.length > 0 && (
          <>
            <div className="sect">CITIES ELEAT IS IN</div>
            <div className="plist loctree">
              {[...new Set(markets.map((m) => m.country))].map((cc) => {
                const cOpen = openCountry === cc;
                /* ⚠️ A CITY THAT IS ITS OWN COUNTRY IS SHOWN ONCE — Al-Noor,
                   21 Oct 2026: "Hong Kong → Hong Kong". When a country holds
                   one city of the same name, the country row opens straight
                   onto that city's areas. */
                const inCc = markets.filter((m) => m.country === cc);
                const sole = inCc.length === 1
                  && (inCc[0].name_en || '').trim().toLowerCase() === String(countryName(cc) || '').trim().toLowerCase()
                  ? inCc[0] : null;
                return (
                  <div key={cc}>
                    <button type="button" className="item" aria-expanded={cOpen}
                      onClick={() => {
                        setOpenCountry(cOpen ? '' : cc);
                        if (sole) { setOpenCity(cOpen ? '' : sole.code); loadAreas(sole.code); }
                      }}>
                      <span className="lab"><span className="t">{countryName(cc)}</span></span>
                      <span className={cOpen ? 'chev open' : 'chev'} aria-hidden="true">›</span>
                    </button>
                    {cOpen && markets.filter((m) => m.country === cc).map((m) => {
                      const name = zh(m.name_en, m.name_zh) || m.code;
                      const mOpen = openCity === m.code;
                      const here = marketFor(centre) === m.code;
                      const areas = areasBy[m.code] || [];
                      return (
                        <div key={m.code}>
                          {sole ? null : (
                          <button type="button" className={here ? 'item sub on' : 'item sub'}
                            aria-expanded={mOpen}
                            onClick={() => { setOpenCity(mOpen ? '' : m.code); loadAreas(m.code); }}>
                            <span className="lab"><span className="t">{name}</span></span>
                            <span className={mOpen ? 'chev open' : 'chev'} aria-hidden="true">›</span>
                          </button>
                          )}
                          {mOpen && (
                            <div className="chipbox locareas">
                              <button type="button" className={here ? 'chip on' : 'chip'}
                                onClick={() => { chooseCity(m); go(back()); }}>
                                {zh(m.all_market_label_en, m.all_market_label_zh
                                  /* the server's own Chinese form (locations.service.js): 全 + the city */
                                  || (m.all_market_label_en && m.name_zh ? '全' + m.name_zh : null)) || name}
                              </button>
                              {areas.map((a) => (
                                <button type="button" key={a.id} className="chip"
                                  onClick={() => {
                                    chooseCity(m);
                                    go(`?explore=1&area=${encodeURIComponent(a.id)}`
                                      + `&areaname=${encodeURIComponent(zh(a.name_en, a.name_zh))}`);
                                  }}>
                                  {zh(a.name_en, a.name_zh)} <b>{a.venues}</b>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {presets.length > 0 && (
          <>
            <div className="sect">HOW FAR “NEAR ME” REACHES</div>
            <div className="chipbox">
              {presets.map((p) => (
                <span key={p.code || p.label} className="chip">{p.label || p.name}</span>
              ))}
            </div>
            {/* ⚠️ THE LOCKED SCREEN'S OWN ADMISSION, kept because it is still
                true: choosing an area sets the centre, and distance filtering
                is not switched on. Quietly dropping it would be a promise the
                app does not keep. */}
            {/* ⚠️⚠️ THIS USED TO END WITH AN ADMISSION THAT DISTANCE FILTERING
                was not switched on, and that is no longer true — 25 Sep 2026.
                `max_distance_km` now reaches both Home's rails and Explore's
                search, measured from the active centre. Al asked for the line
                to go the moment it stopped being true, and leaving a false
                admission on a screen is worse than never having made it. */}
            <p className="mut">Choosing an area sets this.</p>
          </>
        )}
      </main>

      {/* nothing lit: Location is reached from Home's pin as often as from
          Profile, and lighting Profile told somebody they were somewhere
          they had not gone */}
      <TabBar active={null} />
    </div>
  );
}
